import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Sparkles, RefreshCw, Eye, EyeOff, Calendar, CheckCircle2, ArrowUp, ArrowDown, Palette, Monitor, LayoutGrid, ExternalLink } from "lucide-react";
import api from "@/lib/api";

/**
 * /settings/public-copy — Edit every static text that appears on the
 * client-facing pages (/estimate and /proposal). Includes:
 *   - Section-by-section editable textareas with live preview
 *   - "Melhorar com IA" button per field (POST /api/ai/improve-text)
 *   - Sticky Save action
 */

// Field configuration — one entry per editable string.
// ``preview`` describes where the text will appear so the admin doesn't have
// to guess. ``rows`` controls the textarea height. ``aiContext`` guides the
// "Melhorar com IA" call.
const FIELDS = [
  {
    section: "Hero da Estimativa",
    icon: "hero",
    items: [
      {
        key: "estimate_hero_kicker",
        label: "Kicker (linha superior em maiúsculas)",
        rows: 1,
        aiContext: "Kicker do hero da página de estimativa. Curto, em letras espaçadas.",
      },
      {
        key: "estimate_hero_title",
        label: "Título principal",
        rows: 2,
        hint: "Podes usar quebra de linha para dividir em 2 partes.",
        aiContext: "Título principal do hero da página pública de estimativa. Máx 2 linhas.",
      },
    ],
  },
  {
    section: "Reunião Técnica · agendada (data confirmada)",
    icon: "scheduled",
    items: [
      { key: "rt_scheduled_title", label: "Título", rows: 1,
        aiContext: "Título do bloco RT quando a reunião já está agendada." },
      { key: "rt_scheduled_body", label: "Corpo",
        rows: 4, aiContext: "Descrição do que vai ser discutido na Reunião Técnica. Sem falar em contacto futuro — a reunião já está marcada." },
    ],
  },
  {
    section: "Reunião Técnica · aceite (sem data)",
    icon: "accepted",
    items: [
      { key: "rt_accepted_title", label: "Título", rows: 1,
        aiContext: "Título do bloco RT quando o cliente aceitou mas ainda não agendou." },
      { key: "rt_accepted_body", label: "Corpo", rows: 4,
        aiContext: "Descrição do que vai ser discutido na Reunião Técnica." },
      { key: "rt_accepted_footer", label: "Rodapé (linha final)", rows: 2,
        aiContext: "Frase de fecho quando a reunião foi confirmada mas ainda falta agendar dia e hora." },
    ],
  },
  {
    section: "Reunião Técnica · já realizada",
    icon: "completed",
    items: [
      { key: "rt_completed_title", label: "Título", rows: 1,
        aiContext: "Título do bloco RT depois de a reunião ter sido feita — o cliente já recebeu a visita ao domicílio." },
      { key: "rt_completed_body", label: "Corpo", rows: 4,
        aiContext: "Mensagem para quando a RT já foi realizada e a estimativa foi ajustada. Fala do próximo passo (proposta final)." },
      { key: "rt_completed_footer", label: "Rodapé (linha final)", rows: 2,
        aiContext: "Fecho depois da RT — normalmente um agradecimento por ter recebido a equipa em casa." },
    ],
  },
  {
    section: "Reunião Técnica · convite (cliente ainda a decidir)",
    icon: "invite",
    items: [
      { key: "rt_invite_title", label: "Título", rows: 1,
        aiContext: "Título do bloco RT quando o cliente ainda não aceitou." },
      { key: "rt_invite_body", label: "Corpo", rows: 5,
        aiContext: "Reforçar o valor da Reunião Técnica: consulta gratuita, sem compromisso, plano por medida, potencial redução do investimento." },
      { key: "rt_invite_footer", label: "Rodapé (linha final)", rows: 2,
        aiContext: "Convite para agendar / contactar. Aparece por cima dos botões de telefone/email." },
    ],
  },
  {
    section: "Contactos (rodapé do bloco convite)",
    icon: "contact",
    items: [
      { key: "contact_phone", label: "Número de telefone (mostrado)", rows: 1 },
      { key: "contact_phone_href", label: "Link `tel:` (href do botão)", rows: 1,
        hint: "Ex.: tel:+351210000000 — sem espaços." },
      { key: "contact_email", label: "Email de contacto", rows: 1 },
    ],
  },
  {
    section: "Rodapé geral",
    icon: "footer",
    items: [
      { key: "footer_tagline", label: "Tagline", rows: 1,
        aiContext: "Frase-assinatura curta que aparece no rodapé (ex: Cuidamos com dignidade)." },
    ],
  },
  {
    section: "Estimativa · Cartão principal (opção sugerida)",
    icon: "primary-card",
    items: [
      { key: "est_primary_kicker", label: "Kicker do cartão (antes do nome da opção)", rows: 1,
        aiContext: "Kicker curto acima do valor destacado (ex: Sugestão Caring)." },
      { key: "est_primary_suffix_monthly", label: "Sufixo mensal (após o preço)", rows: 1 },
      { key: "est_primary_suffix_total", label: "Sufixo total (após o preço pontual)", rows: 1 },
      { key: "est_label_days_per_week", label: "Label: Dias/semana", rows: 1 },
      { key: "est_label_hours_per_day", label: "Label: Horas/dia", rows: 1 },
      { key: "est_label_hourly_price", label: "Label: Preço/hora", rows: 1 },
      { key: "est_label_weekly_value", label: "Label: Valor semanal", rows: 1 },
      { key: "est_label_total_days", label: "Label: Dias efetivos (pontual)", rows: 1 },
      { key: "est_label_extra_fee", label: "Label: Extra aplicado (pontual)", rows: 1 },
    ],
  },
  {
    section: "Estimativa · Período do serviço (pontual)",
    icon: "service-window",
    items: [
      { key: "est_service_window_kicker", label: "Kicker do bloco", rows: 1 },
      { key: "est_service_window_start", label: "Label: Início", rows: 1 },
      { key: "est_service_window_end", label: "Label: Fim", rows: 1 },
      { key: "est_service_window_total_days", label: "Label: Total de dias", rows: 1 },
    ],
  },
  {
    section: "Estimativa · Outras opções + Ajuste RT",
    icon: "others",
    items: [
      { key: "est_other_options_kicker", label: "Kicker: Outras opções", rows: 1,
        aiContext: "Kicker acima da grelha de opções alternativas." },
      { key: "est_qt_adjusted_kicker", label: "Kicker: Alteração após RT", rows: 1 },
      { key: "est_qt_before_label", label: "Label: Estimativa inicial", rows: 1 },
      { key: "est_qt_after_label", label: "Label: Valor final", rows: 1 },
      { key: "est_qt_reason_label", label: "Label: Motivo do ajuste", rows: 1 },
      { key: "est_qt_confirmed_text", label: "Badge quando valores confirmados sem alteração", rows: 1 },
    ],
  },
  {
    section: "Estimativa · Notas, condições e Próximos passos",
    icon: "footer-est",
    items: [
      { key: "est_notes_kicker", label: "Kicker: Notas do Caring", rows: 1 },
      { key: "est_terms_kicker", label: "Kicker: Condições comerciais", rows: 1 },
      { key: "est_terms_continuidade", label: "Label do tipo: Continuidade", rows: 1 },
      { key: "est_terms_pontual", label: "Label do tipo: Pontual", rows: 1 },
      { key: "est_next_steps_title", label: "Título: Próximos passos", rows: 1 },
      // ---- Status labels (i18n-friendly) ----
      { key: "est_status_done", label: "Estado: Concluído (Passo 1)", rows: 1 },
      { key: "est_status_awaiting", label: "Estado: Aguarda agendamento (Passo 2, RT não agendada)", rows: 1 },
      { key: "est_status_scheduled", label: "Estado: Agendada (Passo 2, RT agendada)", rows: 1 },
      { key: "est_status_pending", label: "Estado: Pendente (Passo 3, RT não agendada)", rows: 1 },
      { key: "est_status_preparing", label: "Estado: Em preparação (Passo 3, RT agendada)", rows: 1 },
      // ---- Step 1: Estimativa ----
      { key: "est_step1_title", label: "Passo 1 — Título", rows: 1 },
      { key: "est_step1_body", label: "Passo 1 — Texto (estado independente)", rows: 4,
        aiContext: "Descrição do passo 'Estimativa' na página pública da estimativa. Registo institucional, sem pressão de venda, transmite acompanhamento e profissionalismo. Terminologia obrigatória: Atendimento, Gestor de Cliente, Reunião Técnica, Proposta Final. Palavras a evitar: qualquer variante ligada a vendas ou processo de venda." },
      // ---- Step 2: Reunião Técnica ----
      { key: "est_step2_title", label: "Passo 2 — Título", rows: 1 },
      { key: "est_step2_body_awaiting", label: "Passo 2 — Texto quando a RT AINDA NÃO está agendada", rows: 6,
        hint: "Podes usar `{phone}` e `{email}` que serão substituídos pelo contacto oficial (ver secção Contacto).",
        aiContext: "Passo 'Reunião Técnica' na página da estimativa, quando ainda não há data agendada. Convida o cliente a contactar o Atendimento. Registo institucional. Terminologia obrigatória: Atendimento, Gestor de Cliente, Reunião Técnica, Proposta Final." },
      { key: "est_step2_body_scheduled", label: "Passo 2 — Texto quando a RT JÁ está agendada", rows: 6,
        hint: "Podes usar `{phone}` e `{email}`.",
        aiContext: "Passo 'Reunião Técnica' quando a data já foi combinada com o cliente. Explica o que vai acontecer na visita. Registo institucional. Terminologia obrigatória: Atendimento, Gestor de Cliente, Reunião Técnica, Proposta Final." },
      // ---- Step 3: Proposta Final e Início do Serviço ----
      { key: "est_step3_title", label: "Passo 3 — Título", rows: 1 },
      { key: "est_step3_body_pending", label: "Passo 3 — Texto quando a RT AINDA NÃO está agendada", rows: 4,
        aiContext: "Passo 'Proposta Final e Início do Serviço' — descrição do que virá depois da RT quando ainda não há RT agendada. Registo institucional. Terminologia obrigatória: Reunião Técnica, Proposta Final, Plano Individual de Cuidados." },
      { key: "est_step3_body_preparing", label: "Passo 3 — Texto quando a RT JÁ está agendada", rows: 4,
        aiContext: "Passo 'Proposta Final e Início do Serviço' — descrição do que virá depois da RT quando a RT já está marcada. Registo institucional. Terminologia obrigatória: Reunião Técnica, Proposta Final, Plano Individual de Cuidados." },
      { key: "est_recipient_label", label: "Cabeçalho: Documento personalizado para", rows: 1 },
    ],
  },
  {
    section: "Plano de Cuidados · Hero e banners",
    icon: "care-plan-hero",
    items: [
      { key: "cp_hero_kicker", label: "Kicker do hero", rows: 1 },
      { key: "cp_hero_title", label: "Título do hero", rows: 2,
        aiContext: "Título do hero do Plano de Cuidados (curto, humano)." },
      { key: "cp_hero_subtitle_prefix", label: "Prefixo do subtítulo (antes dos nomes dos clientes)", rows: 1,
        hint: "Ex: 'Preparado para' → 'Preparado para Senhora Dona Rosa e Senhor Manuel.'" },
      { key: "cp_provisional_title", label: "Banner PROVISÓRIO — título/bold", rows: 1 },
      { key: "cp_provisional_body", label: "Banner PROVISÓRIO — corpo", rows: 3,
        aiContext: "Explicação amável do porquê do plano estar provisório e o que vai acontecer a seguir." },
      { key: "cp_approved_body", label: "Banner VALIDADO — texto (versão anexada automaticamente)", rows: 1 },
      { key: "cp_pdf_button_label", label: "Botão descarregar PDF", rows: 1 },
    ],
  },
  {
    section: "Plano de Cuidados · Secções",
    icon: "care-plan-sections",
    items: [
      { key: "cp_manager_kicker", label: "Kicker do cartão do Gestor", rows: 1 },
      { key: "cp_manager_default_title", label: "Cargo default do Gestor (fallback)", rows: 1 },
      { key: "cp_objectives_title", label: "Título: Objetivos do serviço", rows: 1 },
      { key: "cp_priorities_title", label: "Título: Prioridades identificadas", rows: 1 },
      { key: "cp_activities_title", label: "Título: Atividades diárias", rows: 1 },
    ],
  },
  {
    section: "Plano de Cuidados · Estado bloqueado (aguarda gestor)",
    icon: "care-plan-locked",
    items: [
      { key: "cp_locked_kicker", label: "Kicker", rows: 1 },
      { key: "cp_locked_title", label: "Título", rows: 1 },
      { key: "cp_locked_body", label: "Corpo", rows: 3,
        aiContext: "Mensagem tranquilizadora para o cliente enquanto aguarda a validação da Gestora." },
      { key: "cp_locked_footer", label: "Rodapé (linha final com contacto)", rows: 2 },
    ],
  },
];


// Render helper — turns \n into <br/> for the preview panel
const Br = ({ text }) => (
  <>
    {(text || "").split("\n").map((line, i, arr) => (
      <span key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </span>
    ))}
  </>
);


// Editable color palette — 7 CSS variables that drive the public pages.
const COLORS = [
  { key: "color_bg", label: "Fundo geral", hint: "Cor de fundo principal da página pública." },
  { key: "color_surface", label: "Superfície / Cartões", hint: "Fundo dos cartões (opção primária, RT, etc.)." },
  { key: "color_accent", label: "Acento", hint: "Dourado Caring — títulos, ícones, CTAs." },
  { key: "color_accent_soft", label: "Acento (hover)", hint: "Variação clara do acento (estados hover)." },
  { key: "color_text", label: "Texto principal", hint: "Texto em fundo escuro." },
  { key: "color_text_muted", label: "Texto secundário", hint: "Legendas, kickers, labels em minúsculas." },
  { key: "color_border", label: "Bordas subtis", hint: "Linhas divisórias. Aceita hex com alpha (ex: #FFFFFF1A)." },
];

// Ordered sections of the public estimate page — user can reorder these
// via arrows or reset to defaults.
const SECTION_META = {
  hero: { label: "Hero (kicker + título + resumo)", icon: "🎯" },
  primary_option: { label: "Opção principal (destaque com preço)", icon: "💎" },
  service_window: { label: "Período do serviço pontual (se aplicável)", icon: "📅" },
  other_options: { label: "Outras opções (grid)", icon: "🔀" },
  rt_block: { label: "Bloco Reunião Técnica (variantes)", icon: "🤝" },
  contact_footer: { label: "Contactos (só variante convite)", icon: "📞" },
};


function FieldRow({ item, value, onChange, aiLoading, onImprove }) {
  const inpBase = "w-full bg-card border border-border rounded-md px-3 py-2 text-sm font-body focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 resize-y";
  return (
    <div className="space-y-1.5" data-testid={`copy-field-${item.key}`}>
      <div className="flex items-center justify-between gap-2">
        <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold" htmlFor={`f-${item.key}`}>
          {item.label}
        </label>
        <button type="button"
                 onClick={() => onImprove(item)}
                 disabled={aiLoading[item.key] || !(value || "").trim()}
                 className="inline-flex items-center gap-1.5 text-[11px] rounded-full bg-primary/15 hover:bg-primary/25 border border-primary/40 text-[hsl(var(--warning))] px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                 data-testid={`copy-improve-${item.key}`}
                 title="Reformular com IA mantendo o significado">
          {aiLoading[item.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          Melhorar com IA
        </button>
      </div>
      <textarea id={`f-${item.key}`}
                 value={value}
                 onChange={(e) => onChange(item.key, e.target.value)}
                 rows={item.rows || 3}
                 className={inpBase}
                 data-testid={`copy-input-${item.key}`} />
      {item.hint && <div className="text-[11px] text-[#9C9788]">{item.hint}</div>}
    </div>
  );
}


function PreviewChip({ variant, cfg }) {
  // Renders a small preview card mimicking the public estimate look
  const isDark = variant !== "footer";
  const wrap = isDark
    ? "rounded-2xl border border-primary/25 bg-foreground text-background p-6"
    : "rounded-2xl border border-border bg-card text-foreground p-6";
  const muted = isDark ? "text-primary-foreground/60" : "text-muted-foreground";
  const kicker = isDark ? "text-primary" : "text-foreground";

  if (variant === "hero") {
    return (
      <div className={wrap} data-testid="preview-hero">
        <div className={`text-[9px] uppercase tracking-[0.3em] ${kicker} font-semibold mb-2`}>
          {cfg.estimate_hero_kicker || "—"}
        </div>
        <div className="font-display text-2xl leading-tight" data-testid="preview-hero-title">
          <Br text={cfg.estimate_hero_title} />
        </div>
      </div>
    );
  }
  if (variant === "scheduled") {
    return (
      <div className={wrap} data-testid="preview-scheduled">
        <Calendar className="w-5 h-5 text-primary mb-2" />
        <div className="font-display text-xl mb-1">{cfg.rt_scheduled_title || "—"}</div>
        <div className="text-primary text-xs font-display mb-3">sexta-feira, 10 de abril de 2026 às 14:30 · com Maria Silva</div>
        <div className={`text-sm leading-relaxed ${muted}`}>
          <Br text={cfg.rt_scheduled_body} />
        </div>
      </div>
    );
  }
  if (variant === "accepted") {
    return (
      <div className={wrap} data-testid="preview-accepted">
        <CheckCircle2 className="w-5 h-5 text-primary mb-2" />
        <div className="font-display text-xl mb-2">{cfg.rt_accepted_title || "—"}</div>
        <div className={`text-sm leading-relaxed ${muted} mb-2`}>
          <Br text={cfg.rt_accepted_body} />
        </div>
        <div className={`text-xs ${muted}`}><Br text={cfg.rt_accepted_footer} /></div>
      </div>
    );
  }
  if (variant === "invite") {
    return (
      <div className={wrap} data-testid="preview-invite">
        <Sparkles className="w-5 h-5 text-primary mb-2" />
        <div className="font-display text-xl mb-2">{cfg.rt_invite_title || "—"}</div>
        <div className={`text-sm leading-relaxed ${muted} mb-2`}>
          <Br text={cfg.rt_invite_body} />
        </div>
        <div className={`text-xs ${muted} mb-3`}><Br text={cfg.rt_invite_footer} /></div>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 bg-primary text-foreground rounded-full px-3 py-1 text-[11px] font-semibold">
            {cfg.contact_phone}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-card/10 border border-white/20 rounded-full px-3 py-1 text-[11px]">
            {cfg.contact_email}
          </span>
        </div>
      </div>
    );
  }
  if (variant === "footer") {
    return (
      <div className={`${wrap} text-center`} data-testid="preview-footer">
        <div className="text-[11px] text-muted-foreground">© Caring · {cfg.footer_tagline}</div>
      </div>
    );
  }
  return null;
}


export default function PublicCopySettings() {
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState({});
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewToken, setPreviewToken] = useState(null);
  const [previewMode, setPreviewMode] = useState("live"); // "live" | "chips"
  const [iframeReady, setIframeReady] = useState(false);
  const iframeRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/public-copy");
      setCfg(r.data);
      setInitial(JSON.stringify(r.data));
    } catch (e) {
      toast.error("Erro ao carregar textos");
    }
  };
  useEffect(() => { load(); }, []);

  // Fetch a demo estimate token for the live iframe preview. If no lead
  // qualifies (empty DB), gracefully fall back to the static chips mode.
  useEffect(() => {
    api.get("/public-copy/preview-token")
      .then((r) => {
        const t = r?.data?.token;
        if (t) {
          setPreviewToken(t);
        } else {
          setPreviewMode("chips");
        }
      })
      .catch(() => setPreviewMode("chips"));
  }, []);

  // Listen for the iframe signaling it is ready to receive cfg updates,
  // so the initial state is pushed even if the iframe loaded after the
  // first debounce tick.
  useEffect(() => {
    const handler = (e) => {
      if (e?.data?.type === "caring-preview-ready") {
        setIframeReady(true);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Debounced push of the current cfg to the iframe so the admin sees
  // live changes ~250ms after they stop typing.
  useEffect(() => {
    if (!cfg || previewMode !== "live" || !iframeRef.current) return;
    const t = setTimeout(() => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "caring-preview-cfg", cfg }, "*",
        );
      } catch (_) {}
    }, 250);
    return () => clearTimeout(t);
  }, [cfg, previewMode, iframeReady]);

  const setField = (key, val) => setCfg((c) => ({ ...c, [key]: val }));

  const onImprove = async (item) => {
    if (!cfg?.[item.key]) return;
    setAiLoading((s) => ({ ...s, [item.key]: true }));
    try {
      const r = await api.post("/ai/improve-text", {
        text: cfg[item.key],
        context: item.aiContext || "",
        tone: "consultivo",
      });
      if (r.data?.text) {
        setField(item.key, r.data.text);
        toast.success(`"${item.label}" melhorado`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao melhorar com IA");
    } finally {
      setAiLoading((s) => ({ ...s, [item.key]: false }));
    }
  };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await api.put("/public-copy", cfg);
      setInitial(JSON.stringify(cfg));
      toast.success("Textos guardados — visíveis nas próximas visitas às páginas públicas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm("Repor todos os textos aos valores por defeito da Caring?")) return;
    // Blank the record — backend will fall back to model defaults on next GET
    try {
      // Post empty singleton via PUT with default reload
      await api.put("/public-copy", {});
      await load();
      toast.success("Textos repostos aos defaults");
    } catch (e) {
      // The backend requires all fields — do a soft reset by fetching defaults
      const dummy = {};
      Object.values(FIELDS).flatMap((s) => s.items || []).forEach((it) => (dummy[it.key] = ""));
      // Just reload from server (which auto-fills with defaults if empty)
      await load();
    }
  };

  if (!cfg) {
    return (
      <div className="p-8 max-w-6xl mx-auto text-muted-foreground text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar textos das páginas cliente…
      </div>
    );
  }

  const dirty = JSON.stringify(cfg) !== initial;

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto space-y-6" data-testid="public-copy-root">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Configuração</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Textos das Páginas Cliente</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
            Edite todos os textos <b>estáticos</b> que aparecem nas páginas públicas da estimativa e proposta. Cada alteração fica visível imediatamente na próxima visita do cliente aos links.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {previewToken && (
            <div className="hidden md:inline-flex items-center rounded-full border border-border bg-card overflow-hidden" data-testid="preview-mode-toggle">
              <button type="button"
                       onClick={() => setPreviewMode("live")}
                       className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 ${previewMode === "live" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted/40"}`}
                       data-testid="preview-mode-live">
                <Monitor className="w-3.5 h-3.5" /> Preview ao vivo
              </button>
              <button type="button"
                       onClick={() => setPreviewMode("chips")}
                       className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 ${previewMode === "chips" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted/40"}`}
                       data-testid="preview-mode-chips">
                <LayoutGrid className="w-3.5 h-3.5" /> Cartões
              </button>
            </div>
          )}
          <button onClick={() => setPreviewOpen((v) => !v)}
                   className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 text-muted-foreground"
                   data-testid="copy-toggle-preview">
            {previewOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {previewOpen ? "Esconder preview" : "Mostrar preview"}
          </button>
          <button onClick={reset}
                   className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 text-muted-foreground"
                   data-testid="copy-reset">
            <RefreshCw className="w-3.5 h-3.5" /> Repor defaults
          </button>
          <button onClick={save} disabled={saving || !dirty}
                   className="inline-flex items-center gap-1.5 text-xs bg-foreground hover:bg-[#1E293B] text-background font-semibold rounded-full px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                   data-testid="copy-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Guardar
          </button>
        </div>
      </div>

      <div className={`grid gap-6 ${previewOpen ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {/* -------- EDITOR COLUMN -------- */}
        <div className="space-y-6">
          {/* ==== Palette (colors) ==== */}
          <div className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="copy-section-palette">
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold border-b border-[#F1F5F9] pb-2 flex items-center gap-2">
              <Palette className="w-3.5 h-3.5" /> Paleta de cores das páginas públicas
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COLORS.map((c) => (
                <div key={c.key} className="flex items-center gap-3" data-testid={`color-row-${c.key}`}>
                  <input type="color"
                          value={(cfg[c.key] || "#000000").slice(0, 7)}
                          onChange={(e) => setField(c.key, e.target.value)}
                          className="w-10 h-10 rounded-md border border-border cursor-pointer shrink-0"
                          data-testid={`color-picker-${c.key}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{c.label}</span>
                    </div>
                    <input type="text"
                            value={cfg[c.key] || ""}
                            onChange={(e) => setField(c.key, e.target.value)}
                            placeholder="#RRGGBB"
                            className="mt-1 w-full bg-card border border-border rounded-md px-2 py-1 text-xs font-mono focus:border-primary focus:outline-none"
                            data-testid={`color-hex-${c.key}`} />
                    {c.hint && <div className="text-[10px] text-[#9C9788] mt-1">{c.hint}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ==== Sections order ==== */}
          <div className="rounded-2xl border border-border bg-card p-6 space-y-3" data-testid="copy-section-order">
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold border-b border-[#F1F5F9] pb-2">
              Ordem das secções (estimativa pública)
            </div>
            <div className="text-[11px] text-muted-foreground mb-1">
              Use as setas para reordenar. Secções condicionais (ex: <b>Período do serviço pontual</b>) só aparecem quando aplicáveis, mesmo estando aqui listadas.
            </div>
            <ul className="space-y-2" data-testid="sections-order-list">
              {(cfg.sections_order || []).map((sec, idx, arr) => (
                <li key={sec} className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2" data-testid={`section-item-${sec}`}>
                  <span className="text-lg leading-none">{SECTION_META[sec]?.icon || "•"}</span>
                  <span className="flex-1 text-sm text-foreground">{SECTION_META[sec]?.label || sec}</span>
                  <div className="flex items-center gap-1">
                    <button type="button" disabled={idx === 0}
                             onClick={() => {
                               const next = [...arr];
                               [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                               setField("sections_order", next);
                             }}
                             className="w-7 h-7 inline-flex items-center justify-center rounded-md bg-card border border-border text-muted-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed"
                             data-testid={`section-up-${sec}`}
                             title="Mover para cima">
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" disabled={idx === arr.length - 1}
                             onClick={() => {
                               const next = [...arr];
                               [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                               setField("sections_order", next);
                             }}
                             className="w-7 h-7 inline-flex items-center justify-center rounded-md bg-card border border-border text-muted-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed"
                             data-testid={`section-down-${sec}`}
                             title="Mover para baixo">
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {FIELDS.map((sec) => (
            <div key={sec.section} className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid={`copy-section-${sec.icon}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold border-b border-[#F1F5F9] pb-2">
                {sec.section}
              </div>
              {sec.items.map((it) => (
                <FieldRow key={it.key}
                           item={it}
                           value={cfg[it.key] || ""}
                           onChange={setField}
                           aiLoading={aiLoading}
                           onImprove={onImprove} />
              ))}
            </div>
          ))}
        </div>

        {/* -------- PREVIEW COLUMN -------- */}
        {previewOpen && (
          <div className="space-y-4 lg:sticky lg:top-6 self-start" data-testid="preview-column">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.25em] text-foreground font-semibold">
                {previewMode === "live" && previewToken ? "Preview ao vivo (página real)" : "Preview (visual aproximado)"}
              </div>
              {previewMode === "live" && previewToken && (
                <div className="flex items-center gap-2">
                  <button type="button"
                           onClick={() => {
                             if (iframeRef.current) {
                               // Force full reload of the iframe to re-fetch
                               // lead data (in case DB changed). The cfg is
                               // re-sent automatically on the ready signal.
                               setIframeReady(false);
                               iframeRef.current.src = iframeRef.current.src;
                             }
                           }}
                           className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground"
                           data-testid="preview-iframe-reload"
                           title="Recarregar dados do lead demo">
                    <RefreshCw className="w-3 h-3" /> Recarregar
                  </button>
                  <a href={`/estimate/${previewToken}`}
                     target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground"
                     data-testid="preview-open-new-tab"
                     title="Abrir estimativa demo em nova aba">
                    <ExternalLink className="w-3 h-3" /> Nova aba
                  </a>
                </div>
              )}
            </div>

            {previewMode === "live" && previewToken ? (
              <>
                <div className="rounded-2xl border border-border bg-foreground overflow-hidden shadow-lg" data-testid="preview-iframe-wrap">
                  <iframe ref={iframeRef}
                           src={`/estimate/${previewToken}?preview=1`}
                           title="Preview da estimativa pública"
                           className="w-full block"
                           style={{ height: "calc(100vh - 180px)", minHeight: "560px", border: "0" }}
                           data-testid="preview-iframe" />
                </div>
                <div className="text-[11px] text-[#9C9788] leading-relaxed">
                  Alterações refletem-se ~250ms após parar de digitar. Este preview usa uma lead real; secções condicionais (ex.: <b>Período do serviço</b>) só aparecem se a lead as tiver.
                </div>
              </>
            ) : (
              <div className="space-y-6">
                <PreviewChip variant="hero" cfg={cfg} />
                <PreviewChip variant="scheduled" cfg={cfg} />
                <PreviewChip variant="accepted" cfg={cfg} />
                <PreviewChip variant="invite" cfg={cfg} />
                <PreviewChip variant="footer" cfg={cfg} />
                <div className="text-[11px] text-[#9C9788] pt-2">
                  {previewMode === "live" && !previewToken
                    ? "Sem leads elegíveis para preview ao vivo. A mostrar cartões estáticos — o layout final está em /estimate/<token>."
                    : "O visual final na página pública pode variar ligeiramente. Estas caixas mostram o essencial dos textos e ordem."}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
